import { pgSelect, pgInsert, pgUpdate, pgDelete } from './supabaseClient';
import { logEvent } from './debugLog';
import type { Vec3 } from '../types';

/**
 * Visitor-submitted POI bookmarks from the Ask NavMe chat agent
 * (suggest_poi tool — see supabase/functions/concierge). Anonymous
 * visitors CAN write here (unlike `pois`, which is staff-only — see RLS on
 * both tables), but only ever as status:'pending', and there is no public
 * read policy, so a suggestion is invisible to everyone but staff until
 * reviewed. Staff approve (→ promoted into a real POI via
 * poiCreationService.createPoiWithNode) or reject from the Dashboard's
 * Suggestions card. See migration poi_suggestions_via_chat.
 */

export interface PoiSuggestion {
  id: string;
  map_code: string;
  name: string;
  category: string | null;
  description: string | null;
  floor: number;
  x: number;
  y: number;
  z: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export interface NewPoiSuggestion {
  mapCode: string;
  name: string;
  category?: string;
  description?: string;
  floor: number;
  position: Vec3;
}

/** Anonymous visitors + staff: submit a bookmark after tap-to-place. */
export async function submitPoiSuggestion(input: NewPoiSuggestion): Promise<PoiSuggestion> {
  const row = await pgInsert<PoiSuggestion>('poi_suggestions', {
    map_code: input.mapCode,
    name: input.name.slice(0, 60),
    category: input.category?.slice(0, 40) || null,
    description: input.description?.slice(0, 200) || null,
    floor: input.floor,
    x: input.position.x,
    y: input.position.y,
    z: input.position.z,
    status: 'pending',
  });
  logEvent('session', `poi suggestion submitted: "${input.name}"`);
  return row;
}

/** Staff-only (RLS-enforced): pending suggestions for the active venue. */
export async function getPendingSuggestions(mapCode: string): Promise<PoiSuggestion[]> {
  return pgSelect<PoiSuggestion>(
    'poi_suggestions',
    `select=*&map_code=eq.${encodeURIComponent(mapCode)}&status=eq.pending&order=created_at.desc`,
  );
}

/** Staff-only: marks a suggestion approved (call AFTER promoting it into a real POI). */
export async function markSuggestionApproved(id: string): Promise<void> {
  await pgUpdate('poi_suggestions', id, { status: 'approved' });
  logEvent('admin', `poi suggestion approved: ${id}`);
}

/** Staff-only: rejects (deletes) a suggestion — nothing worth keeping around once dismissed. */
export async function markSuggestionRejected(id: string): Promise<void> {
  await pgDelete('poi_suggestions', id);
  logEvent('admin', `poi suggestion rejected: ${id}`);
}
